import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Star, Award, Download, Search, Mic } from "lucide-react";
import NavbarScanner from "../cart/NavbarScanner";
import NavbarCart from "../cart/NavbarCart";
import Navbar from "../components/Navbar";
import config from "../../config/config";
import Card from "../components/sellModule/Card";
// import Modal from "../components/sellModule/Modal";
// import CartItem from "../components/sellModule/CartItem";
// import Form from "../components/sellModule/Form";

import { fetchBooks, setBook } from "../../ReduxSlice/BookSlice";
import useDebounce from "../../common/useDebounce";
import BarcodeScannerComponent from "react-qr-barcode-scanner";
const { API_BASE_URL } = config;
function App() {
  const dispatch = useDispatch();
  const allBooks = useSelector((state) => state.books.bookData);

  const recognitionRef = useRef(null);
  const inputFocus = useRef(null);
  const [isListening, setIsListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch] = useDebounce(searchInput, 600);
  const [scanMode, setScanMode] = useState(false);
  const [filteredBooks, setFilteredBooks] = useState([]);
  const [openAndCloseModal, setOpenAndCloseModal] = useState(false);
  const [paymentMode, setPaymentMode] = useState("");

  // ✅ Load books once
  useEffect(() => {
    if (!allBooks.length) {
      dispatch(fetchBooks());
    }
  }, [allBooks.length, dispatch]);

  // ✅ Voice recognition (unchanged)
  useEffect(() => {
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.lang = "en-US";
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsListening(true); // 🔴 start indicator
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setSearchInput(transcript);
    };

    recognition.onerror = () => {
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false); // 🔴 stop indicator
    };

    recognitionRef.current = recognition;
  }, []);

  const startVoiceSearch = () => recognitionRef.current?.start();
  const handleInput = (e) => setSearchInput(e.target.value);
  const handleModal = () => setOpenAndCloseModal(!openAndCloseModal);
  const handlePayment = (mode) => setPaymentMode(mode);

  // ✅ Barcode API fetch (unchanged)
  const searchByBarcode = async (barcode) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${API_BASE_URL}/barcode/GetBookByBarcode?barcode=${barcode}`,
      );
      if (!res.ok) throw new Error("Book not found");

      const book = await res.json();

      setFilteredBooks([book]);
      dispatch(setBook([book]));
    } catch (err) {
      console.error("Barcode fetch error:", err);
      setFilteredBooks([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ MAIN FIX — Unified Search Logic
  useEffect(() => {
    const text = debouncedSearch?.trim().toLowerCase();

    if (!text) {
      setFilteredBooks([]);
      setLoading(false);
      return;
    }
    setLoading(true);

    // ✅ CHANGE 1: Try barcode match FIRST for ANY value
    const scannedBook = allBooks.find(
      (book) =>
        String(book.barcodeValue).trim().toLowerCase() === text ||
        String(book.id).trim().toLowerCase() === text,
    );

    if (scannedBook) {
      setFilteredBooks([scannedBook]);
      setLoading(false);
      return;
    }

    // ✅ CHANGE 2: If input looks like barcode (number/long string) → call API
    if (/^[0-9A-Za-z-]{6,}$/.test(text)) {
      searchByBarcode(text);
      return;
    }

    // ✅ Normal name/author search (unchanged)
    const filtered = allBooks.filter(
      (book) =>
        book.booknameinenglish?.toLowerCase().includes(text) ||
        book.author?.toLowerCase().includes(text),
    );

    setFilteredBooks(filtered);
    setLoading(false);
  }, [debouncedSearch, allBooks]);

  // ✅ CHANGE 3: Fix barcode compare inside scanner also
  const handleScan = async (err, result) => {
    if (err) return;

    if (result?.text) {
      setScanMode(false);
      const barcode = result.text.trim();
      setSearchInput(barcode);

      const scannedBook = allBooks.find(
        (book) =>
          String(book.barcodeValue).trim() === barcode ||
          String(book.id).trim() === barcode,
      );

      if (scannedBook) {
        setFilteredBooks([scannedBook]);
      } else {
        await searchByBarcode(barcode);
      }
    }
  };

  return (
    <div className="min-h-screen w-full p-0 bg-gradient-to-b from-gray-200 to-gray-300 text-white">
      <Navbar
        onSearchClick={() => inputFocus.current.focus()}
        onScanClick={() => setScanMode(true)}
        openModal={handleModal}
        paymentMode={handlePayment}
      />

      {/* Barcode Scanner Modal */}
      {scanMode && (
        <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/70 p-4">
          <div className="w-full max-w-md bg-gray-900 rounded-lg p-4">
            <button
              onClick={() => setScanMode(false)}
              className="text-red-500 mb-2 font-semibold"
            >
              Close
            </button>
            <BarcodeScannerComponent
              width={300}
              height={300}
              onUpdate={handleScan}
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full px-4 md:px-8 py-10">
        <div className="flex flex-col items-center text-center gap-10">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            //transition={{ duration: 0.5 }}
            className="max-w-4xl space-y-6"
          >
            <h1 className="text-4xl md:text-6xl text-gray-800 font-bold leading-tight">
              Discover the Power of{" "}
              <span className="text-purple-600">Knowledge</span>
            </h1>

            {/*Cart + Search + Scanner Row */}
            <div className="flex items-center gap-4 w-full">
              <div className="hidden sm:block shrink-0">
                <NavbarCart openModal={handleModal} paymentMode={paymentMode} />
              </div>

              {/* Search Input */}
              <div className="flex-1 relative">
                <input
                  type="text"
                  ref={inputFocus}
                  value={searchInput}
                  onChange={handleInput}
                  placeholder="Search books or scan barcode..."
                  className="w-full px-6 py-3 pl-12 pr-12 bg-white border border-gray-800 rounded-full text-gray-800"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                {/* Mic Icon */}
                <Mic
                  onClick={startVoiceSearch}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 cursor-pointer transition
    ${isListening ? "text-red-600 animate-pulse scale-125" : "text-purple-600 hover:scale-110"}
  `}
                />
              </div>

              {/* Scanner Button */}
              <div className="hidden sm:block shrink-0">
                <NavbarScanner onScanClick={() => setScanMode(true)} />
              </div>
            </div>
          </motion.div>

          {/* Books List */}
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-14 w-14 border-4 border-purple-600 border-t-transparent"></div>
            </div>
          ) : (
            <Card allBooks={filteredBooks} />
          )}

          {/* Features */}

          {!loading && filteredBooks.length === 0 && (
            <motion.div className="grid grid-cols-3 gap-4 px-2 md:px-0 pointer-events-none opacity-80">
              {[
                { Icon: Star, color: "text-yellow-500", title: "Best Seller" },
                {
                  Icon: Award,
                  color: "text-purple-500",
                  title: "Award Winner",
                },
                {
                  Icon: Download,
                  color: "text-green-500",
                  title: "Instant Access",
                },
              ].map(({ Icon, color, title }, i) => (
                <motion.div
                  key={i}
                  whileHover={{ scale: 1.04 }}
                  className="
        bg-white
        border border-gray-200
        shadow-sm
        hover:shadow-md
        rounded-xl
        p-3 sm:p-4
        flex flex-col items-center text-center
        transition
      "
                >
                  <Icon className={`w-8 h-8 sm:w-12 sm:h-12 ${color} mb-3`} />
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800">
                    {title}
                  </h3>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default App;